import type { PlottedPagePdf, SinglePagePdf } from "@pdfking/pdfengine"
import { VirtualFileType } from "@pdfking/pdfengine"
import { Init } from "@pdfking/reactivity/preact"
import type { Media, RequestRaster } from "@pdfking/renderer-api"
import type { StringGUID } from "@pdfking/tfunc"
import { RecordMerge } from "@pdfking/tfunc"

/** A page drawn once for the whole plot, over the extent its media place it
 * in, the trim grown by the bleed: a split page by its raster's url, a plotted
 * page by itself — its shapes drawn as the plot's own (see MediaDefs). */
export type MediaImage = Media & ({ url: string } | { plot: PlottedPagePdf })

/**
 * The plot's pages, each made once however many media show it — an n-up
 * sheet, a spread, a poster running across slots — and shared by every
 * viewer alike: the flat plot's `<use>`s point at the one def of it (see
 * MediaDefs), the book's textures take the same raster. Keyed by the page's
 * content guid: a file replaced under the same name is a new page, a
 * generated page the same one as long as its request is.
 */
export const CreateMediaImages = () => {
  // One raster url per page, the first asker's
  const rasters = new Map<StringGUID, ReturnType<RequestRaster>>()
  // Each page's def, once it is drawn
  const shown = new Map<StringGUID, Promise<MediaImage | undefined>>()

  const store = Init({
    data: {
      images: {} as Record<StringGUID, MediaImage>,
    },
    methods: {
      // The page's raster, made by `make` the first time it is asked for and
      // the same instance ever after
      raster(guid: StringGUID, make: () => ReturnType<RequestRaster>): ReturnType<RequestRaster> {
        const existing = rasters.get(guid)
        if (existing) return existing
        const raster = make()
        rasters.set(guid, raster)
        return raster
      },
      // The page's def, drawn once its file is at hand: a plotted page as
      // itself, any other by its raster. Answers it, if there is one to use.
      image(
        guid: StringGUID,
        media: Media,
        get_file: () => Promise<SinglePagePdf>,
        rasterize: (file: SinglePagePdf) => ReturnType<RequestRaster>
      ): Promise<MediaImage | undefined> {
        const existing = shown.get(guid)
        if (existing) return existing
        const drawn = get_file().then(async file => {
          if (file.enum === VirtualFileType.PLOTTED_PAGE_PDF) {
            const image = { ...media, plot: file }
            this.images = RecordMerge(this.images, { [guid]: image })
            return image
          }
          const url = await this.raster(guid, () => rasterize(file))
          if (!url) return undefined
          const image = { ...media, url }
          this.images = RecordMerge(this.images, { [guid]: image })
          return image
        })
        shown.set(guid, drawn)
        return drawn
      },
    },
  })

  return store
}

export type MediaImages = ReturnType<typeof CreateMediaImages>
